<script setup>
import { useStore } from "vuex";
import { computed, onMounted } from "vue";
import ArtworkHistoryModal from "components/jiffy/vue-product-details-dtf/ArtworkHistoryModal.vue";
import { useDtfStore } from "components/jiffy/vue-product-details-dtf/store";
import Placement from "./placementComponents/Placement.vue";
import { MODES, SURFACES } from "./misc/constants";

const store = useStore();
const enableMonetization = computed(() =>
  Boolean(store.getters.config.enable_ab_made_monetization_v2)
);
const dtfStore = enableMonetization.value ? useDtfStore() : null;
const supportsMade = computed(() => store.getters["app/supportsMade"]);
const hideCreation = computed(() => store.getters.config.enable_ab_dtf_hide_your_creation);
const isDedicatedPdp = computed(() => store.getters.config.made_dedicated_pdp);
const isBlanks = computed(() => store.getters["app/mode"] === MODES.BLANKS);
const artworkExists = computed(() => ({
  front: store.getters["artwork_front/artworkExists"] || store.getters["artwork_front/touched"],
  back: store.getters["artwork_back/artworkExists"] || store.getters["artwork_back/touched"]
}));
const isConditionalReview = computed(() => !artworkExists.value.front && !artworkExists.value.back);
const isPreview = computed(() => isBlanks.value || isConditionalReview.value);

onMounted(() => {
  store.dispatch("loadCanvaSettings");
});
</script>

<template>
  <Teleport :disabled="!isBlanks" to="#js-product-details-made-placeholder">
    <div v-if="!supportsMade" class="product-details-print-warning" data-testid="warning-message">
      <span class="product-details-print-warning__icon">!</span>
      <span class="product-details-print-warning__text">
        This product color is not supported in Jiffy Custom Shirts.
      </span>
    </div>

    <div class="product-details-print">
      <div v-if="isDedicatedPdp" class="product-details-print__header">
        <h3 class="product-details-print__title">Upload your images</h3>
        <div class="product-details-print__pricing">
          <div class="product-details-print__price">+$4.49</div>
          <div class="product-details-print__location-text">For second location</div>
        </div>
      </div>
      <div
        class="product-details-print__grid-wrapper"
        :class="{
          'product-details-print__grid-wrapper--two-columns': isPreview,
          'product-details-print__grid-wrapper--spaced': isConditionalReview
        }"
        data-testid="placement-grid"
      >
        <div class="product-details-print__placement">
          <Placement
            title-prefix="Front"
            side-key="front"
            :is-preview="isPreview"
            :hide-creation="hideCreation"
            :enable-monetization="enableMonetization"
            :available-placements="[SURFACES.FRONT, SURFACES.FRONT_LEFT_CHEST]"
          >
            <template #design-upload>
              <slot name="design-upload-front"></slot>
            </template>
          </Placement>
        </div>

        <slot name="after-front"></slot>

        <div
          class="product-details-print__placement product-details-print__placement--back js-pdp-back-artwork-placement"
        >
          <Placement
            title-prefix="Back"
            side-key="back"
            :is-preview="isPreview"
            :hide-creation="hideCreation"
            :enable-monetization="enableMonetization"
            :available-placements="[SURFACES.BACK]"
          >
            <template #design-upload>
              <slot name="design-upload-back"></slot>
            </template>
          </Placement>
        </div>

        <slot name="after-back"></slot>
      </div>
    </div>
    <template v-if="enableMonetization">
      <ArtworkHistoryModal
        v-if="dtfStore?.showArtworkHistoryModal"
        :index="store.getters.artworkHistoryModalIndex"
        :single-selection-mode="true"
      />
    </template>
  </Teleport>
</template>

<style scoped lang="scss">
@import "frontend/init/jiffy/mixin";
@import "frontend/init/pod/settings";

.product-details-print {
  $this: &;

  container: container / inline-size;
  position: relative;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
    border-radius: 4px;
    background: linear-gradient(90deg, $link-water-3 6%, $white 121%);
  }

  &__title {
    margin: 0;
    padding: 6px 16px;
    color: $gray-text;
    font-size: 20px;
    flex: 1;
  }

  &__pricing {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    margin-left: 16px;
    margin-right: 8px;
    text-align: right;
    padding: 5px 1px;
    line-height: normal;

    @include with-container(container, $pod-pdp-desktop-response) {
      flex-direction: row;
      align-items: center;
      gap: 8px;
    }
  }

  &__price {
    color: $punch;
    font-size: 22px;
    font-weight: 700;
    white-space: nowrap;
  }

  &__location-text {
    padding: 2px 4px;
    border-radius: 2px;
    background-color: $neutral-black;
    color: $white;
    font-size: 11px;
    font-weight: 700;
    white-space: nowrap;
  }

  &__grid-wrapper {
    &--two-columns {
      @include with-container(container, $pod-pdp-desktop-response) {
        display: grid;
        gap: 38px;
        grid-template-columns: calc(50% - 19px) calc(50% - 19px);

        #{$this}__placement--back {
          margin-top: 0;
        }
      }
    }

    &--spaced {
      margin-bottom: 30px;
    }
  }

  &__placement {
    &--back {
      margin-top: 20px;

      @include with-container(container, $pod-pdp-desktop-response) {
        margin-top: 28px;
      }
    }
  }
}

.product-details-print-warning {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  padding: 6px 12px;
  border: 1px solid $warning-yellow;
  border-radius: 5px;
  background-color: $yellow-alert-background;

  &__icon {
    width: 22px;
    height: 22px;
    margin-right: 4px;
    border: 2px solid currentColor;
    border-radius: 50%;
    color: $warning-yellow;
    font-size: 14px;
    font-weight: 700;
    line-height: 18px;
    text-align: center;
  }

  &__text {
    color: $black;
    font-size: 13px;
  }
}
</style>
